import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { MapBaseSource, MapboxStyleWithZoom } from '@connect-services/client/components/map/map.component';
import { LayerStylesService } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class SourcesService {
    public readonly sources = signal<MapBaseSource<unknown>[]>([]);

    private readonly layerStyles = new Map<string, MapboxStyleWithZoom>();
    private readonly layerStylesService = inject(LayerStylesService);
    private readonly nilUuid = '00000000-0000-0000-0000-000000000000';

    public async getLayerStyle(layerStyleId: string | null): Promise<MapboxStyleWithZoom> {
        if (this.layerStyles.has(layerStyleId ?? this.nilUuid)) {
            return this.layerStyles.get(layerStyleId ?? this.nilUuid)!;
        }

        const response = await firstValueFrom(
            this.layerStylesService.layerStyleControllerGetOne({
                id: layerStyleId ?? this.nilUuid,
            }),
        );

        const style = {
            id: layerStyleId ?? this.nilUuid,
            maxzoom: response.maxzoom,
            minzoom: response.minzoom,
            style: response.style,
        };
        this.layerStyles.set(layerStyleId ?? this.nilUuid, style);

        return style;
    }

    public getSource(sourceId: string): MapBaseSource<unknown> | undefined {
        return this.sources().find((source: MapBaseSource<unknown>) => source.sourceId === sourceId);
    }

    public getSourcesByIds(sourceIds: string[]): MapBaseSource<unknown>[] {
        if (!sourceIds.length) {
            return [];
        }

        return sourceIds
            .map((sourceId: string) => this.getSource(sourceId))
            .filter((source): source is MapBaseSource<unknown> => !!source);
    }

    public resetLayerStyles(): void {
        this.layerStyles.clear();
    }
}
